import * as React from "react"
import { useTranslation } from "react-i18next"
import { Button, EmptyState, Icon, cn } from "@nexus/ui"
import { PersonasOnboardingCard } from "../PersonasOnboardingCard/PersonasOnboardingCard"
import type { PersonasOnboardingCardVariant } from "../PersonasOnboardingCard/persona-onboarding-card.types"
import { humanizePersonaTitle } from "../../lib/persona-format"
import type { PersonaOnboardingReviewItem } from "../../types/personas.types"

/** Horizontal offset (px) for peek neighbors — tuned to ~720px card width. */
const PEEK_OFFSET_PX = 360
const SIDE_SCALE = 0.9
const SIDE_OPACITY = 0.42
/** Keep ±2 mounted so cards can slide in/out instead of popping. */
const RENDER_RADIUS = 2
const RING_RADIUS = 32
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS
/** Swipe distance before advancing (px). */
const SWIPE_THRESHOLD_PX = 56
/**
 * `size="sm"` is 32px, under the 44px touch minimum, and this empty state is the only
 * way out of the step. Widen to 44px unless a fine pointer is present, matching the
 * device-keyed idiom used across the app rather than a viewport breakpoint — a touch
 * laptop is wide but still needs the bigger target.
 */
const TOUCH_TARGET_CLASS = "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8"

export type PersonasOnboardingReviewVariant = PersonasOnboardingCardVariant

export interface PersonasOnboardingReviewProps {
  items: PersonaOnboardingReviewItem[]
  focusIndex: number
  onFocusIndexChange: (index: number) => void
  isBusy?: boolean
  canContinue: boolean
  continueHint: string
  /** Brand = coverage gate; industry = optional adds, finish always available. */
  variant?: PersonasOnboardingReviewVariant
  /**
   * Brand accepted share used as market-coverage stub until industry BQ lands.
   * Null when there is no brand pool to measure — renders a placeholder.
   */
  marketCoveragePct?: number | null
  onAccept: (item: PersonaOnboardingReviewItem) => void
  onReject: (item: PersonaOnboardingReviewItem) => void
  onUndo: (item: PersonaOnboardingReviewItem) => void
  onContinue: () => void
  onBack?: () => void
  isContinuing?: boolean
  /** Re-runs suggestion discovery — surfaced on the empty state when none were found. */
  onRetry?: () => void
  isRetrying?: boolean
}

/** Brand / industry review — Figma coverage bar + animated peek carousel. */
export function PersonasOnboardingReview({
  items,
  focusIndex,
  onFocusIndexChange,
  isBusy = false,
  canContinue,
  continueHint,
  variant = "brand",
  marketCoveragePct = null,
  onAccept,
  onReject,
  onUndo,
  onContinue,
  onBack,
  isContinuing = false,
  onRetry,
  isRetrying = false,
}: PersonasOnboardingReviewProps) {
  const { t } = useTranslation()
  const isIndustry = variant === "industry"
  const total = items.length
  const safeIndex = total === 0 ? 0 : Math.min(focusIndex, total - 1)
  const reviewed = items.filter((i) => i.decisionStatus !== "PENDING").length
  const accepted = React.useMemo(
    () => items.filter((i) => i.decisionStatus === "ACCEPTED"),
    [items],
  )
  const pending = total - reviewed

  const poolSpend = React.useMemo(
    () => items.reduce((sum, i) => sum + i.persona.spend, 0),
    [items],
  )
  const poolAds = React.useMemo(
    () => items.reduce((sum, i) => sum + i.persona.adCount, 0),
    [items],
  )
  const acceptedSpend = React.useMemo(
    () => accepted.reduce((sum, i) => sum + i.persona.spend, 0),
    [accepted],
  )
  const coveragePct = poolSpend > 0 ? Math.round((acceptedSpend / poolSpend) * 100) : 0
  const ringPct = isIndustry ? marketCoveragePct : coveragePct
  const coverageDash = `${((ringPct ?? 0) / 100) * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`

  const goPrev = React.useCallback(() => {
    if (total === 0) return
    onFocusIndexChange((safeIndex - 1 + total) % total)
  }, [onFocusIndexChange, safeIndex, total])

  const goNext = React.useCallback(() => {
    if (total === 0) return
    onFocusIndexChange((safeIndex + 1) % total)
  }, [onFocusIndexChange, safeIndex, total])

  const tablistRef = React.useRef<HTMLDivElement>(null)

  const handleDotKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (total === 0) return
      const next = dotKeyTarget(event.key, safeIndex, total)
      if (next === null) return
      event.preventDefault()
      onFocusIndexChange(next)
      // The dot nodes are already mounted, so focus can move before the
      // re-render that hands the new tab its tabIndex=0.
      const tabs = tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      tabs?.[next]?.focus()
    },
    [onFocusIndexChange, safeIndex, total],
  )

  const trackCards = React.useMemo(() => {
    if (total === 0) return [] as Array<{
      item: PersonaOnboardingReviewItem
      index: number
      delta: number
    }>
    return items
      .map((item, index) => ({
        item,
        index,
        delta: circularDelta(index, safeIndex, total),
      }))
      .filter(({ delta }) => Math.abs(delta) <= RENDER_RADIUS)
  }, [safeIndex, items, total])

  const swipe = useCarouselSwipe({
    enabled: total > 1 && !isBusy,
    onPrev: goPrev,
    onNext: goNext,
  })

  if (total === 0) {
    return (
      <section
        data-testid={
          isIndustry ? "personas-onboarding-industry-empty" : "personas-onboarding-review-empty"
        }
        className="mx-auto flex w-full max-w-lg flex-col items-center py-16"
      >
        <EmptyState
          icon="users"
          title={t(
            isIndustry
              ? "personas.onboarding.industry.emptyTitle"
              : "personas.onboarding.review.emptyTitle",
          )}
          description={t(
            isIndustry
              ? "personas.onboarding.industry.emptyDescription"
              : "personas.onboarding.review.emptyDescription",
          )}
          action={
            isIndustry ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                {onBack ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={TOUCH_TARGET_CLASS}
                    onClick={onBack}
                    disabled={isContinuing || isBusy}
                  >
                    {t("personas.onboarding.industry.back", "Back")}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  className={TOUCH_TARGET_CLASS}
                  iconEnd="arrow-right"
                  onClick={onContinue}
                  aria-busy={isContinuing}
                  disabled={!canContinue || isContinuing}
                >
                  {isContinuing
                    ? t("personas.onboarding.industry.finishing", "Finishing…")
                    : t("personas.onboarding.industry.finish", "Finish setup")}
                </Button>
              </div>
            ) : onRetry ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={TOUCH_TARGET_CLASS}
                iconStart="refresh-cw"
                onClick={onRetry}
                aria-busy={isRetrying}
                disabled={isRetrying}
              >
                {isRetrying ? t("common.retrying", "Retrying…") : t("common.retry", "Retry")}
              </Button>
            ) : undefined
          }
        />
      </section>
    )
  }

  return (
    <section
      data-testid={isIndustry ? "personas-onboarding-industry" : "personas-onboarding-review"}
      data-variant={variant}
      className={cn(
        "flex flex-col gap-4",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-300 motion-safe:fill-mode-both motion-reduce:animate-none",
      )}
    >
      {isIndustry ? (
        <IndustryBar
          coveragePct={marketCoveragePct}
          coverageDash={coverageDash}
          addedCount={accepted.length}
          reviewed={reviewed}
          total={total}
          positionLabel={t("personas.onboarding.industry.personaOf", {
            current: safeIndex + 1,
            total,
          })}
          onBack={onBack}
          isBusy={isBusy}
          isContinuing={isContinuing}
        />
      ) : (
        <CoverageBar
          coveragePct={coveragePct}
          coverageDash={coverageDash}
          acceptedCount={accepted.length}
          pendingCount={pending}
          positionLabel={t("personas.onboarding.review.personaOf", {
            current: safeIndex + 1,
            total,
          })}
          continueHint={continueHint}
        />
      )}

      <ReviewProgressBar reviewed={reviewed} total={total} />

      {/* Padding keeps card shadows inside overflow-hidden (sides + bottom bleed). */}
      <div
        className="relative h-[min(600px,78vh)] touch-pan-y overflow-hidden px-4 pb-10 pt-3"
        onPointerDown={swipe.onPointerDown}
        onPointerMove={swipe.onPointerMove}
        onPointerUp={swipe.onPointerUp}
        onPointerCancel={swipe.onPointerCancel}
      >
        {trackCards.map(({ item, index: i, delta }) => {
          const isFocused = delta === 0
          const absDelta = Math.abs(delta)
          const sharePct = isIndustry
            ? poolAds > 0
              ? (item.persona.adCount / poolAds) * 100
              : null
            : poolSpend > 0
              ? (item.persona.spend / poolSpend) * 100
              : null
          const scale = isFocused ? 1 : SIDE_SCALE
          const opacity = isFocused ? 1 : absDelta === 1 ? SIDE_OPACITY : 0
          const primaryAction = isFocused
            ? {
                label: isContinuing
                  ? isIndustry
                    ? t("personas.onboarding.industry.finishing", "Finishing…")
                    : t("personas.onboarding.review.continuing", "Continuing…")
                  : isIndustry
                    ? t("personas.onboarding.industry.finish", "Finish setup")
                    : t("personas.onboarding.review.continue", "Continue to industry"),
                onClick: onContinue,
                disabled: !canContinue || isBusy,
                isLoading: isContinuing,
              }
            : undefined

          return (
            <div
              key={item.persona.id}
              className={cn(
                "absolute top-3 left-1/2 h-[540px] w-[min(720px,92vw)] will-change-transform",
                "transition-[transform,opacity] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                "motion-reduce:transition-none",
              )}
              style={{
                transform: `translateX(calc(-50% + ${delta * PEEK_OFFSET_PX}px)) scale(${scale})`,
                opacity,
                zIndex: 20 - absDelta,
                pointerEvents: absDelta > 1 ? "none" : "auto",
              }}
            >
              <PersonasOnboardingCard
                item={item}
                sharePct={sharePct}
                variant={variant}
                isFocused={isFocused}
                isBusy={isBusy && isFocused}
                onFocus={() => onFocusIndexChange(i)}
                onAccept={() => onAccept(item)}
                onReject={() => onReject(item)}
                onUndo={() => onUndo(item)}
                primaryAction={primaryAction}
              />
            </div>
          )
        })}

        <NavButton
          ariaLabel={t("personas.onboarding.review.prevAria", "Previous persona")}
          onClick={goPrev}
          disabled={total <= 1 || isBusy}
          direction="prev"
          className="absolute top-1/2 left-1.5 z-50 -translate-y-1/2"
        />
        <NavButton
          ariaLabel={t("personas.onboarding.review.nextAria", "Next persona")}
          onClick={goNext}
          disabled={total <= 1 || isBusy}
          direction="next"
          className="absolute top-1/2 right-1.5 z-50 -translate-y-1/2"
        />
      </div>

      <div
        ref={tablistRef}
        className="flex flex-wrap items-center justify-center gap-1.5"
        role="tablist"
        aria-label={t("personas.onboarding.review.dotsAria", "Persona review progress")}
        onKeyDown={handleDotKeyDown}
      >
        {items.map(({ persona, decisionStatus }, i) => {
          const isActive = i === safeIndex
          return (
            <button
              key={persona.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              aria-label={t("personas.onboarding.review.dotAria", {
                current: i + 1,
                total,
                title: humanizePersonaTitle(persona.title),
              })}
              data-status={decisionStatus}
              className={cn(
                "h-2 rounded-full transition-[width,background-color] duration-300 ease-out motion-reduce:transition-none",
                isActive ? "w-5" : "w-2 hover:opacity-80",
                dotColorClass(decisionStatus, isActive, variant),
              )}
              onClick={() => onFocusIndexChange(i)}
            />
          )
        })}
      </div>
    </section>
  )
}

/** Shortest signed distance on a circular index ring (−floor(n/2) … +floor(n/2)). */
function circularDelta(index: number, focus: number, total: number): number {
  let delta = index - focus
  const half = Math.floor(total / 2)
  if (delta > half) delta -= total
  if (delta < -half) delta += total
  return delta
}

/** Index the WAI-ARIA tabs pattern moves to, or null when the key is not ours. */
function dotKeyTarget(key: string, current: number, total: number): number | null {
  switch (key) {
    case "ArrowRight":
      return (current + 1) % total
    case "ArrowLeft":
      return (current - 1 + total) % total
    case "Home":
      return 0
    case "End":
      return total - 1
    default:
      return null
  }
}

function dotColorClass(
  status: PersonaOnboardingReviewItem["decisionStatus"],
  isActive: boolean,
  variant: PersonasOnboardingReviewVariant,
): string {
  if (status === "ACCEPTED") {
    if (variant === "industry") {
      return isActive ? "bg-[var(--competitor)]" : "bg-[var(--competitor)]/55"
    }
    return isActive ? "bg-success" : "bg-success/55"
  }
  if (status === "REJECTED") return isActive ? "bg-error" : "bg-error/55"
  if (variant === "industry") {
    return isActive ? "bg-[var(--competitor)]" : "bg-muted-foreground/30"
  }
  return isActive ? "bg-primary" : "bg-muted-foreground/30"
}

/** Same ring geometry in both bars — only the progress stroke and center label differ. */
function CoverageRing({
  coverageDash,
  strokeClassName,
  children,
}: {
  coverageDash: string
  strokeClassName: string
  children: React.ReactNode
}) {
  return (
    <div className="relative size-16 shrink-0">
      <svg width="64" height="64" viewBox="0 0 80 80" className="-rotate-90" aria-hidden>
        <circle
          cx="40"
          cy="40"
          r={RING_RADIUS}
          fill="none"
          className="stroke-border"
          strokeWidth="9"
        />
        <circle
          cx="40"
          cy="40"
          r={RING_RADIUS}
          fill="none"
          className={cn(
            strokeClassName,
            "transition-[stroke-dasharray] duration-600 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
          )}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={coverageDash}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-mono text-[15px] font-medium tabular-nums text-foreground">
          {children}
        </span>
      </div>
    </div>
  )
}

function ReviewProgressBar({ reviewed, total }: { reviewed: number; total: number }) {
  const { t } = useTranslation()
  const pct = total > 0 ? Math.round((reviewed / total) * 100) : 0
  return (
    <div className="flex flex-col gap-1.5" data-testid="personas-onboarding-progress">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-muted-foreground">
          {t("personas.onboarding.review.progressLabel", {
            defaultValue: "{{reviewed}} of {{total}} decided",
            reviewed,
            total,
          })}
        </p>
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={reviewed}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function CoverageBar({
  coveragePct,
  coverageDash,
  acceptedCount,
  pendingCount,
  positionLabel,
  continueHint,
}: {
  coveragePct: number
  coverageDash: string
  acceptedCount: number
  pendingCount: number
  positionLabel: string
  continueHint: string
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-5 rounded-lg border border-border bg-card px-5 py-3.5">
      <CoverageRing coverageDash={coverageDash} strokeClassName="stroke-primary">
        {t("personas.onboarding.review.coveragePct", { pct: coveragePct })}
      </CoverageRing>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold text-foreground">
          {t("personas.onboarding.review.coverageTitle", "Coverage data")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("personas.onboarding.review.coverageDetail", {
            pct: coveragePct,
            accepted: acceptedCount,
            pending: pendingCount,
          })}
        </p>
        <p className="text-[11px] font-medium text-muted-foreground">{continueHint}</p>
      </div>

      <span className="shrink-0 font-mono text-xs whitespace-nowrap text-muted-foreground tabular-nums">
        {positionLabel}
      </span>
    </div>
  )
}

function IndustryBar({
  coveragePct,
  coverageDash,
  addedCount,
  reviewed,
  total,
  positionLabel,
  onBack,
  isBusy,
  isContinuing,
}: {
  /** Null when the brand pool it is derived from is empty. */
  coveragePct: number | null
  coverageDash: string
  addedCount: number
  reviewed: number
  total: number
  positionLabel: string
  onBack?: () => void
  isBusy: boolean
  isContinuing: boolean
}) {
  const { t } = useTranslation()
  const hasCoverage = coveragePct !== null
  return (
    <div className="flex flex-wrap items-center gap-5 rounded-lg border border-[var(--competitor)]/20 bg-card px-5 py-3.5">
      <CoverageRing coverageDash={coverageDash} strokeClassName="stroke-[var(--competitor)]">
        {hasCoverage
          ? t("personas.onboarding.review.coveragePct", { pct: coveragePct })
          : t("personas.onboarding.review.coverageUnknown", "—")}
      </CoverageRing>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-foreground">
            {t("personas.onboarding.industry.title", "Personas from your industry")}
          </p>
          <span className="inline-flex items-center rounded-sm bg-[var(--violet-subtle)] px-1.5 py-0.5 text-[10px] font-medium leading-none text-[var(--violet-subtle-foreground)]">
            {t("personas.onboarding.industry.marketBadge", "MARKET")}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {hasCoverage
            ? t("personas.onboarding.industry.detail", { pct: coveragePct })
            : t(
                "personas.onboarding.industry.detailUnknown",
                "Personas competing for the same customers in your industry. Add the ones worth tracking.",
              )}
        </p>
        <p className="text-[11px] font-medium text-muted-foreground">
          {t("personas.onboarding.industry.addedCount", { count: addedCount })}
          {hasCoverage ? (
            <>
              <span className="mx-1.5 text-border">·</span>
              {t("personas.onboarding.industry.marketCoverage", { pct: coveragePct })}
            </>
          ) : null}
          <span className="mx-1.5 text-border">·</span>
          {t("personas.onboarding.review.progressLabel", {
            defaultValue: "{{reviewed}} of {{total}} decided",
            reviewed,
            total,
          })}
          <span className="mx-1.5 text-border">·</span>
          {positionLabel}
        </p>
      </div>

      {onBack ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onBack}
          disabled={isContinuing || isBusy}
          className="shrink-0"
        >
          {t("personas.onboarding.industry.back", "Back")}
        </Button>
      ) : null}
    </div>
  )
}

function NavButton({
  ariaLabel,
  onClick,
  disabled,
  direction,
  className,
}: {
  ariaLabel: string
  onClick: () => void
  disabled?: boolean
  direction: "prev" | "next"
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "hidden size-11 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-[0_2px_10px_rgba(0,0,0,0.08)]",
        "transition-[opacity,transform,background-color] duration-150 ease-out",
        "hover:bg-muted active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40",
        "motion-reduce:transition-none motion-reduce:active:scale-100",
        "md:inline-flex",
        className,
      )}
    >
      <Icon name={direction === "prev" ? "chevron-left" : "chevron-right"} size="sm" />
    </button>
  )
}

function useCarouselSwipe({
  enabled,
  onPrev,
  onNext,
}: {
  enabled: boolean
  onPrev: () => void
  onNext: () => void
}) {
  const startX = React.useRef<number | null>(null)
  const tracking = React.useRef(false)

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return
      // Ignore drags that start on buttons / inputs so Accept/Reject stay clickable.
      const target = e.target as HTMLElement
      if (target.closest("button, a, textarea, input, [role='tab']")) return
      startX.current = e.clientX
      tracking.current = true
    },
    [enabled],
  )

  const onPointerMove = React.useCallback((e: React.PointerEvent) => {
    if (!tracking.current || startX.current == null) return
    // If the user is clearly scrolling vertically, abandon the swipe.
    if (Math.abs(e.movementY) > Math.abs(e.movementX) * 1.5) {
      tracking.current = false
      startX.current = null
    }
  }, [])

  const finish = React.useCallback(
    (clientX: number) => {
      if (!tracking.current || startX.current == null) return
      const dx = clientX - startX.current
      tracking.current = false
      startX.current = null
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return
      if (dx < 0) onNext()
      else onPrev()
    },
    [onNext, onPrev],
  )

  const onPointerUp = React.useCallback(
    (e: React.PointerEvent) => {
      finish(e.clientX)
    },
    [finish],
  )

  const onPointerCancel = React.useCallback(() => {
    tracking.current = false
    startX.current = null
  }, [])

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel }
}
